// src/components/persons/PersonForm.jsx
import React, { useEffect, useMemo, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Typography,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Fade,
  IconButton,
  Avatar,
  alpha,
  MenuItem
} from "@mui/material";
import {
  Person as PersonIcon,
  LocalTaxi as LocalTaxiIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
} from "@mui/icons-material";

import { usePersonForm } from "../../hooks/usePersons";
import { PersonLocations } from "./PersonLocations";
import { useAuthContext } from "../../context/AuthProvider";
import { DOCUMENT_TYPES } from "../utils/persons/documentTypes";

// Utilidades
const onlyDigits = (v) => (v == null ? "" : String(v).replace(/\D/g, ""));
const trimOrNull = (v) => {
  const s = (v ?? "").trim();
  return s === "" ? null : s;
};

// Esquema de validación
const schema = yup.object({
  pernom: yup.string().transform((v) => (v ?? "").trim()).required("El nombre es obligatorio").max(100, "Máximo 100 caracteres"),
  perape: yup.string().transform((v) => (v ?? "").trim()).required("Los apellidos son obligatorios").max(100, "Máximo 100 caracteres"),
  pertipdoc: yup.string().oneOf(['DNI', 'CE', 'PASAPORTE', 'RUC', 'CARNET_EXTRANJERIA'], 'Tipo de documento inválido').required('El tipo de documento es obligatorio'),
  //perdni: yup.string().transform((v) => onlyDigits(v).trim()).required("El DNI es obligatorio").length(8, "El DNI debe tener 8 dígitos"),
  perdni: yup
    .string()
    .transform((v) => onlyDigitsAndLetters(v))
    .required("El número de documento es obligatorio")
    .max(20, "Máximo 20 caracteres")
    .test('documento-formato', 'Formato inválido', function (value) {
      const tipo = this.parent.pertipdoc;
      if (tipo === 'DNI') return /^\d{8}$/.test(value);
      if (tipo === 'RUC') return /^\d{11}$/.test(value);
      return value && value.length >= 4;
    }),
  pertel: yup.string().transform((v) => trimOrNull(v)).nullable().max(20, "Máximo 20 caracteres").notRequired(),
  perema: yup.string().transform((v) => trimOrNull(v)).nullable().email("E-mail inválido").max(255, "Máximo 255 caracteres").notRequired(),
  perdir: yup.string().transform((v) => trimOrNull(v)).nullable().max(255, "Máximo 255 caracteres").notRequired(),
  perpricod: yup.string().transform((v) => trimOrNull(v)).nullable().matches(/^\d{4,8}$|^$/, "Debe tener entre 4 y 8 dígitos").notRequired(),
  perfecnac: yup.date().transform((value, originalValue) => !originalValue ? null : value).nullable().typeError("Debe ser una fecha válida").notRequired(),
  estreg: yup.string().oneOf(["A", "I"], "El estado debe ser ACTIVO o INACTIVO").required("El estado es obligatorio"),
});

const onlyDigitsAndLetters = (v) => (v == null ? "" : String(v).replace(/[^\dA-Za-z]/g, ""));

const PersonForm = ({ open, personId, handleClose, onNotify }) => {
  const { isEdit, personDetail, isLoadingDetail, createMutation, updateMutation, refreshPerson } = usePersonForm(personId);
  const { user } = useAuthContext();

  // Preparar valores iniciales
  const initialValues = useMemo(() => {
    if (!personDetail && isEdit) return null;
    return {
      pernom: personDetail?.pernom ?? "",
      perape: personDetail?.perape ?? "",
      pertipdoc: personDetail?.pertipdoc ?? "DNI",
      perdni: personDetail?.perdni ?? "",
      pertel: personDetail?.pertel ?? "",
      perema: personDetail?.perema ?? "",
      perdir: personDetail?.perdir ?? "",
      perpricod: personDetail?.perpricod ?? "",
      perfecnac: personDetail?.perfecnac ? personDetail.perfecnac.split('T')[0] : "",
      estreg: personDetail?.estreg ?? "A",
    };
  }, [personDetail, isEdit]);



  // Configurar formulario
  const { control, register, handleSubmit, reset, watch, setValue, formState: { errors, isDirty, isValid } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues || { pernom: "", perape: "", perdni: "", pertel: "", perema: "", perdir: "", perpricod: "", perfecnac: "", estreg: "A" },
    mode: "onChange",
  });

  const pertelValue = watch("pertel");
  const perpricodValue = watch("perpricod");

  const suggestedCode = useMemo(() => {
    const phoneDigits = onlyDigits(pertelValue);  // ← Usar pertelValue del watch
    return phoneDigits && phoneDigits.length >= 4 ? phoneDigits.slice(-4) : "";
  }, [pertelValue]);  // ← Se actualiza cada vez que cambia el teléfono

  const getHelperText = useCallback(() => {
    if (errors.perpricod?.message) return errors.perpricod.message;
    if (perpricodValue) return "Código de seguridad asignado";
    if (suggestedCode) return `Sugerencia: ${suggestedCode}`;
    return "Ingrese teléfono para generar sugerencia";
  }, [errors.perpricod?.message, perpricodValue, suggestedCode]);

  // ✅ Determinar color del helperText
  const getHelperTextColor = useCallback(() => {
    if (errors.perpricod?.message) return "error";
    if (perpricodValue) return "success.main";
    return "text.secondary";
  }, [errors.perpricod?.message, perpricodValue]);

  const handleGenerateSecurityCode = useCallback(() => {
    if (!suggestedCode) {
      onNotify?.("Ingresa un teléfono válido (al menos 4 dígitos) para generar el código.", "warning");
      return;
    }

    const currentCode = perpricodValue;

    if (currentCode !== suggestedCode) {
      setValue("perpricod", suggestedCode, { shouldDirty: true, shouldValidate: true });
      onNotify?.(`Código generado: ${suggestedCode}`, "success");
    } else {
      onNotify?.("El código ya es el mismo.", "info");
    }
  }, [suggestedCode, perpricodValue, setValue, onNotify]);

  useEffect(() => { if (open && initialValues) reset(initialValues); }, [open, initialValues, reset]);
  useEffect(() => { if (!open) setTimeout(() => reset(), 200); }, [open, reset]);

  const handleFormClose = useCallback((shouldRefresh = false) => { reset(); handleClose(shouldRefresh); }, [reset, handleClose]);

  const submit = useCallback(async (data) => {
    const payload = {
      ...data,
      pertel: trimOrNull(data.pertel),
      perema: trimOrNull(data.perema),
      perdir: trimOrNull(data.perdir),
      perpricod: data.perpricod ? onlyDigits(data.perpricod) : null,
      perfecnac: data.perfecnac || null,
      pertipdoc: data.pertipdoc || 'DNI',
      perdni: onlyDigits(data.perdni),
      pernom: (data.pernom ?? "").trim(),
      perape: (data.perape ?? "").trim(),
    };
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: personId, data: { ...payload, updbyid: user.EmpID } });
        onNotify?.("Persona actualizada correctamente.", "success");
        await refreshPerson();
      } else {
        await createMutation.mutateAsync({ ...payload, crebyid: user.EmpID });
        onNotify?.("Persona creada correctamente.", "success");
        handleFormClose(true);
      }
    } catch (err) {
      onNotify?.(err?.response?.data?.error || err?.message || "Error al guardar la persona.", "error");
    }
  }, [isEdit, personId, user, updateMutation, createMutation, refreshPerson, onNotify, handleFormClose]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const direcciones = personDetail?.direcciones || [];
  const primaryAddress = direcciones.find((d) => d.perlocpri);
  const hasDirecciones = direcciones.length > 0;

  // Pantalla de carga
  if (open && isEdit && isLoadingDetail) {
    return (
      <Dialog open={open} fullWidth maxWidth="lg" keepMounted={false}>
        <DialogContent>
          <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">Cargando datos de la persona...</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (isEdit && !personDetail && !isLoadingDetail) return null;

  return (
    <Dialog open={open} onClose={() => handleFormClose(false)} fullWidth maxWidth="lg" keepMounted={false} TransitionComponent={Fade} transitionDuration={300}>
      {/* HEADER MEJORADO */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #2563EB 100%)',
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: alpha('#fff', 0.15), width: 40, height: 40 }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {isEdit ? `Editar persona: ${personId}` : "Nueva persona"}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => handleFormClose(false)} sx={{ color: "white", "&:hover": { bgcolor: alpha('#fff', 0.1) } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ p: 0 }}>
          {/* SECCIÓN DE ESTADÍSTICAS - CHIPS EN EL TOPE CON DISEÑO MEJORADO */}
          <Box sx={{ p: 2.5, pb: 0 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Paper variant="outlined" sx={{ px: 2, py: 0.75, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1, bgcolor: hasDirecciones ? alpha('#2196f3', 0.08) : 'transparent' }}>
                <LocalTaxiIcon fontSize="small" color={hasDirecciones ? "primary" : "action"} />
                <Typography variant="body2" fontWeight={500}>
                  {direcciones.length} dirección{hasDirecciones ? 'es' : ''} registrada{hasDirecciones ? 's' : ''}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ px: 2, py: 0.75, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1, bgcolor: perpricodValue ? alpha('#4caf50', 0.08) : alpha('#ff9800', 0.08) }}>
                <SecurityIcon fontSize="small" color={perpricodValue ? "success" : "warning"} />
                <Typography variant="body2" fontWeight={500}>
                  {perpricodValue ? `Código: ${perpricodValue}` : "Sin código de seguridad"}
                </Typography>
              </Paper>

              {primaryAddress?.locnom && (
                <Paper variant="outlined" sx={{ px: 2, py: 0.75, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1, bgcolor: alpha('#00bcd4', 0.08) }}>
                  <HomeIcon fontSize="small" color="info" />
                  <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 250 }}>
                    Principal: {primaryAddress.locnom}
                  </Typography>
                </Paper>
              )}

              {!isEdit && (
                <Chip label="Nuevo registro" color="secondary" size="small" sx={{ fontWeight: 500 }} />
              )}
            </Stack>
          </Box>

          {/* CONTENIDO PRINCIPAL - DOS COLUMNAS CON MEJOR ESPACIADO */}
          <Box sx={{ p: 2.5, pt: 1 }}>
            <Grid container spacing={3}>
              {/* COLUMNA IZQUIERDA - DATOS PERSONALES */}
              <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: "background.default", borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="primary" />
                    Información personal
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Nombre(s)" fullWidth autoFocus={!isEdit} {...register("pernom")} error={!!errors.pernom} helperText={errors.pernom?.message} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Apellidos" fullWidth {...register("perape")} error={!!errors.perape} helperText={errors.perape?.message} size="small" />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="pertipdoc"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            select
                            label="Tipo documento"
                            fullWidth
                            size="small"
                            {...field}
                            error={!!errors.pertipdoc}
                            helperText={errors.pertipdoc?.message}
                          >
                            {DOCUMENT_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Número de documento"
                        fullWidth
                        inputProps={{ maxLength: 20 }}
                        {...register("perdni")}
                        error={!!errors.perdni}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField label="Fecha nacimiento" type="date" fullWidth InputLabelProps={{ shrink: true }} {...register("perfecnac")} error={!!errors.perfecnac} helperText={errors.perfecnac?.message} size="small" />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField label="Teléfono" fullWidth {...register("pertel")} error={!!errors.pertel} helperText={errors.pertel?.message} size="small" />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField label="Correo electrónico" type="email" fullWidth {...register("perema")} error={!!errors.perema} helperText={errors.perema?.message} size="small" />
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      <TextField label="Dirección (campo legacy)" fullWidth multiline rows={1} {...register("perdir")} error={!!errors.perdir} helperText={errors.perdir?.message || "Para servicios de taxi use las direcciones georreferenciadas"} size="small" />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          label="Código seguridad"
                          fullWidth
                          inputProps={{ inputMode: "numeric", maxLength: 8 }}
                          {...register("perpricod")}
                          error={!!errors.perpricod}
                          size="small"
                          InputLabelProps={{
                            shrink: !!perpricodValue,  // ✅ Forzar que el label se contraiga cuando hay valor
                          }}
                          FormHelperTextProps={{
                            sx: { color: getHelperTextColor() }
                          }}
                          helperText={getHelperText()}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleGenerateSecurityCode}
                          size="small"
                          sx={{ minWidth: 90, textTransform: "none" }}
                          disabled={!suggestedCode || !!perpricodValue}
                        >
                          Generar
                        </Button>
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Controller name="estreg" control={control} render={({ field }) => (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>Estado:</Typography>
                          <ToggleButtonGroup exclusive value={field.value} onChange={(_, val) => val && field.onChange(val)} size="small">
                            <ToggleButton value="A" sx={{ px: 3 }}>Activo</ToggleButton>
                            <ToggleButton value="I" sx={{ px: 3 }}>Inactivo</ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                      )} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* COLUMNA DERECHA - DIRECCIONES TAXI */}
              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: "background.default", borderRadius: 2, border: '1px solid', borderColor: 'divider', height: "100%", display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalTaxiIcon fontSize="small" color="primary" />
                    Direcciones
                  </Typography>

                  {!isEdit ? (
                    <Alert severity="info" icon={<CheckCircleIcon fontSize="small" />} sx={{ borderRadius: 2, flex: 1, alignItems: 'center' }}>
                      Guarda los datos personales para registrar direcciones georreferenciadas
                    </Alert>
                  ) : (
                    <PersonLocations personId={personId} locations={direcciones} onNotify={onNotify} />
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.default", borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={() => handleFormClose(false)} disabled={isSubmitting} variant="outlined" size="medium">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isDirty || !isValid}
            size="medium"
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : isEdit ? <EditIcon /> : <AddIcon />}
            sx={{ minWidth: 180, textTransform: "none", fontWeight: 600, px: 3 }}
          >
            {isSubmitting ? "Guardando..." : isEdit ? "Actualizar persona" : "Crear persona"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PersonForm;